import { CssBaseline } from '@mui/material';
import { ErrorBoundary } from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import log from 'loglevel';
import { SnackbarProvider } from 'notistack';
import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { FormattedMessage } from 'react-intl';
import { RouterProvider } from 'react-router-dom';
import { RecoilRoot, useRecoilValue } from 'recoil';
import { chatDrawerOpenState } from '~/state/atoms/chatAtoms';
import InterpolatedSlicesDialog from './components/Dialogs/InterpolatedSlicesDialog';
import JobQueue from './components/JobQueue';
import WarningSnackbarContent from './components/WarningSnackbarContent';
import FirefoxBlockPage from './containers/errors/FirefoxBlockPage';
import { UnexpectedErrorPage } from './containers/errors/RouteErrorBoundary';
import { AppServicesAndSessionBridge } from './contexts/AppServicesAndSessionBridge';
import { IntlProvider } from './i18n';
import { createMsalInstance } from './lib/msal';
import router from './routes/router';
import { initializeSentry } from './sentry';
import './theme/fonts.css';
import './theme/index.css';
import ThemeProvider from './theme/ThemeProvider';
import AppConfig from './utils/appconfig';
import { initializeItkWasmPipelines } from './lib/elastix';

// dcmjs's "validation.dcmjs" logger emits .error() for every unknown VR it
// encounters during DICOM parsing (e.g. "Invalid vr type xs - using US").
// These are not actionable — dcmjs falls back to a sensible default and
// continues. Silencing only this namespace prevents console flooding and
// keeps these non-actionable warnings out of Sentry's console-capture
// integration while leaving the rest of dcmjs's logging intact.
log.getLogger('validation.dcmjs').setLevel(log.levels.SILENT);

declare module 'notistack' {
    interface VariantOverrides {
        jobQueue: true;
    }
}

/**
 * Wraps `SnackbarProvider` so notifications swap to the bottom-left when the
 * chat drawer is open — otherwise they'd render *behind* the drawer on the
 * right and the "Applied to X" confirmations would be invisible to the user.
 * Lives inside `RecoilRoot` so it can read `chatDrawerOpenState`.
 */
function AppSnackbarProvider({ children }: { children: ReactNode }) {
    const chatDrawerOpen = useRecoilValue(chatDrawerOpenState);
    const horizontal: 'left' | 'right' = chatDrawerOpen ? 'left' : 'right';

    return (
        <SnackbarProvider
            classes={{
                containerAnchorOriginBottomRight:
                    'snackbar-container-bottom-right-default',
                containerAnchorOriginBottomLeft:
                    'snackbar-container-bottom-left-default',
            }}
            maxSnack={3}
            preventDuplicate
            anchorOrigin={{ vertical: 'bottom', horizontal }}
            Components={{
                jobQueue: JobQueue,
                warning: WarningSnackbarContent,
            }}
        >
            {children}
        </SnackbarProvider>
    );
}

const queryClient = new QueryClient();
const root = createRoot(document.getElementById('root')!);
const msalDisabled = import.meta.env.VITE_MSAL_DISABLED === 'true';

// eslint-disable-next-line no-console
console.info(`Workspace+ version ${AppConfig.version}`);

// Initialize ITK-Wasm pipelines for image registration
initializeItkWasmPipelines().catch(console.error);

function isFirefox(): boolean {
    return (
        /firefox/i.test(navigator.userAgent) &&
        !/seamonkey/i.test(navigator.userAgent)
    );
}

const AppProvidersAndRouter = () => {
    if (isFirefox()) {
        return (
            <IntlProvider>
                <ThemeProvider>
                    <CssBaseline />
                    <FirefoxBlockPage
                        title={<FormattedMessage id="firefoxNotSupported" />}
                        stack={
                            <FormattedMessage
                                id="detectedBrowser"
                                values={{ browser: navigator.userAgent }}
                            />
                        }
                    />
                </ThemeProvider>
            </IntlProvider>
        );
    }

    return (
        <IntlProvider>
            <ThemeProvider>
                <CssBaseline />
                <QueryClientProvider client={queryClient}>
                    <AppSnackbarProvider>
                        <ErrorBoundary
                            fallback={<UnexpectedErrorPage stack={undefined} />}
                        >
                            <AppServicesAndSessionBridge>
                                <RouterProvider router={router} />
                            </AppServicesAndSessionBridge>
                            <InterpolatedSlicesDialog />
                        </ErrorBoundary>
                    </AppSnackbarProvider>
                </QueryClientProvider>
            </ThemeProvider>
        </IntlProvider>
    );
};

const AppContent = (
    <RecoilRoot>
        <AppProvidersAndRouter />
    </RecoilRoot>
);

async function initializeAndRenderApp() {
    try {
        await AppConfig.initialize();
    } catch (error) {
        console.error('Failed to load app configuration:', error);
    }

    initializeSentry();

    if (msalDisabled) {
        root.render(<StrictMode>{AppContent}</StrictMode>);
        return;
    }

    if (!AppConfig.clientId || !AppConfig.authority) {
        console.debug('Authentication is disabled.');
        root.render(<StrictMode>{AppContent}</StrictMode>);
        return;
    }

    try {
        const { MsalProvider } = await import('@azure/msal-react');
        const msalInstance = await createMsalInstance();

        root.render(
            <StrictMode>
                <MsalProvider instance={msalInstance}>
                    {AppContent}
                </MsalProvider>
            </StrictMode>
        );
    } catch (error) {
        console.error('MSAL setup or initialization failed:', error);
        console.warn(
            'Proceeding without MSAL authentication due to an error during its setup.'
        );
        root.render(<StrictMode>{AppContent}</StrictMode>);
    }
}

initializeAndRenderApp();
